/** Number of bars drawn in vault audio card previews. */
export const VAULT_AUDIO_WAVEFORM_BAR_COUNT = 52;

/** Downsample PCM samples into normalized peak heights in `[0, 1]`. */
export function peaksFromAudioChannelData(
  channelData: Float32Array,
  barCount = VAULT_AUDIO_WAVEFORM_BAR_COUNT,
): number[] {
  if (channelData.length === 0 || barCount <= 0) return [];

  const blockSize = Math.max(1, Math.floor(channelData.length / barCount));
  const peaks: number[] = [];
  for (let bar = 0; bar < barCount; bar += 1) {
    const start = bar * blockSize;
    const end = Math.min(start + blockSize, channelData.length);
    let peak = 0;
    for (let index = start; index < end; index += 1) {
      peak = Math.max(peak, Math.abs(channelData[index]!));
    }
    peaks.push(peak);
  }

  const max = Math.max(...peaks, 0.001);
  return peaks.map((value) => value / max);
}

/** Decode an audio blob and return normalized waveform peaks for inline previews. */
export async function decodeVaultAudioWaveformPeaks(
  source: ArrayBuffer,
  barCount = VAULT_AUDIO_WAVEFORM_BAR_COUNT,
): Promise<number[] | null> {
  if (typeof AudioContext === "undefined") return null;

  const audioContext = new AudioContext();
  try {
    const copy = source.slice(0);
    const audioBuffer = await audioContext.decodeAudioData(copy);
    const channelData = audioBuffer.getChannelData(0);
    return peaksFromAudioChannelData(channelData, barCount);
  } catch {
    return null;
  } finally {
    void audioContext.close();
  }
}
